import React from 'react'
import { useTranslation } from 'react-i18next'
import { LayoutList, Layers } from 'lucide-react'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import type { FeedWidgets } from '@/types/spaces'

export type FeedScope = 'space' | 'section'

interface FeedModuleFieldsProps {
  value: FeedScope
  onChange: (value: FeedScope) => void
  disabled?: boolean
  className?: string
  /**
   * F-181: the side column's widgets. Passed only to owner and admin (the API answers 403 to anyone
   * else); without it the "Side column" block is not drawn.
   */
  widgets?: FeedWidgets
  onWidgetsChange?: (value: FeedWidgets) => void
}

const WIDGET_KEYS = ['events', 'trending', 'newMembers', 'leaderboard'] as const

/**
 * F-162: what a Feed space lists. "space" shows only the posts made in it;
 * "section" aggregates every space of its section, as feeds always did.
 * The server stores it as `Space.feedScope`, outside `moduleConfig`.
 */
export const FeedModuleFields: React.FC<FeedModuleFieldsProps> = ({ value, onChange, disabled = false, className, widgets, onWidgetsChange }) => {
  const { t } = useTranslation(['common', 'feed'])

  const options = [
    { value: 'space' as const, icon: LayoutList, title: t('modules.settings.feed.scopeSpace'), description: t('modules.settings.feed.scopeSpaceHint') },
    { value: 'section' as const, icon: Layers, title: t('modules.settings.feed.scopeSection'), description: t('modules.settings.feed.scopeSectionHint') },
  ]

  return (
    <div className={cn('space-y-2', className)} data-testid="feed-scope">
      <span className="text-sm font-medium text-gray-700">{t('modules.settings.feed.scope')}</span>
      <div className="grid gap-2 sm:grid-cols-2">
        {options.map(({ value: option, icon: Icon, title, description }) => {
          const selected = value === option
          return (
            <button
              key={option}
              type="button"
              aria-pressed={selected}
              data-value={option}
              disabled={disabled}
              onClick={() => onChange(option)}
              className={cn(
                'flex items-start gap-3 rounded-lg border-2 p-3 text-left transition-all',
                'focus:outline-none focus:ring-2 focus:ring-primary-200 focus:ring-offset-1',
                selected ? 'border-primary bg-primary-50' : 'border-gray-200 bg-white hover:border-gray-300',
                disabled && 'cursor-not-allowed opacity-50'
              )}
            >
              <Icon className={cn('mt-0.5 h-5 w-5 flex-shrink-0', selected ? 'text-primary' : 'text-gray-400')} />
              <span className="min-w-0">
                <span className={cn('block text-sm font-medium', selected ? 'text-primary-700' : 'text-gray-900')}>{title}</span>
                <span className="mt-0.5 block text-xs leading-snug text-gray-500">{description}</span>
              </span>
            </button>
          )
        })}
      </div>

      {widgets && onWidgetsChange && (
        <fieldset className="pt-2" data-testid="feed-widgets">
          <legend className="text-sm font-medium text-gray-700">{t('feed:sidebar.settings.title')}</legend>
          <p className="mt-0.5 text-xs text-gray-500">{t('feed:sidebar.settings.hint')}</p>
          <div className="mt-2 divide-y divide-gray-100 rounded-lg border border-gray-200">
            {WIDGET_KEYS.map((key) => {
              const id = `feed-widget-${key}`
              return (
                <div key={key} className="flex items-start justify-between gap-4 p-3">
                  <label htmlFor={id} className="min-w-0 cursor-pointer">
                    <span className="block text-sm font-medium text-gray-900">{t(`feed:sidebar.settings.${key}`)}</span>
                    <span className="mt-0.5 block text-xs leading-snug text-gray-500">{t(`feed:sidebar.settings.${key}Hint`)}</span>
                  </label>
                  <Switch
                    id={id}
                    data-widget={key}
                    checked={widgets[key]}
                    disabled={disabled}
                    onCheckedChange={(checked) => onWidgetsChange({ ...widgets, [key]: checked })}
                    className="mt-0.5 data-[state=checked]:bg-primary focus-visible:ring-primary"
                  />
                </div>
              )
            })}
          </div>
        </fieldset>
      )}
    </div>
  )
}
